import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Card, Input, PageHeader, Pagination, Select, Table } from '../../design';
import { fmtDateTime } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';
import { useOrgOptions } from '../users/UsersPage';
import {
  OPERATION_STATUS,
  type Operation,
  type OperationStatus,
  OperationStatusBadge,
  recipientOf,
  useCount,
  useLockerOptions,
} from './common';
import { OperationWizard } from './OperationWizard';
import s from './operations.module.css';
import { ExportButton } from '../reports/ExportButton';

function countOps(status: OperationStatus) {
  return () =>
    unwrap(api.GET('/operations', { params: { query: { status, pageSize: 1 } } })) as Promise<{
      total: number;
    }>;
}

export function OperationsPage() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const { values, page, pageSize, set } = useUrlState([
    'q',
    'status',
    'lockerId',
    'orgId',
  ] as const);
  const [creating, setCreating] = useState(false);
  const orgs = useOrgOptions();
  const lockers = useLockerOptions();
  const lockerName = (id: string) => lockers.data?.items.find((l) => l.id === id)?.name ?? '…';

  const pending = useCount(['operations', 'pending'], countOps('pending'));
  const ready = useCount(['operations', 'ready'], countOps('ready'));
  const expired = useCount(['operations', 'expired'], countOps('expired'));
  const done = useCount(['operations', 'done'], countOps('done'));

  const query = useQuery({
    queryKey: ['operations', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/operations', {
          params: {
            query: queryParams({
              q: values.q || undefined,
              status: (values.status || undefined) as OperationStatus | undefined,
              lockerId: values.lockerId || undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Operațiuni"
        subtitle="De la autorizarea depunerii la confirmarea ridicării: un flux complet, trasabil."
        actions={
          <span className="flex">
            <ExportButton
              kind="operations"
              params={{
                q: values.q || undefined,
                status: values.status || undefined,
                lockerId: values.lockerId || undefined,
                orgId: values.orgId || undefined,
              }}
            />
            {has('operations') && (
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                Operațiune nouă
              </Button>
            )}
          </span>
        }
      />
      <div className={s.kpis}>
        <Kpi label="Așteaptă încărcare" value={pending.total} hint="Chei de depunere emise" />
        <Kpi
          label="Pregătite de ridicare"
          value={ready.total}
          hint="Compartimente ocupate și autorizate"
        />
        <Kpi
          label="Termen depășit"
          value={expired.total}
          hint="Conținutul rămâne în compartiment"
          tone="red"
        />
        <Kpi label="Finalizate" value={done.total} hint="Ridicare și închidere confirmate" />
      </div>
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Input
            placeholder="Caută după titlu, referință sau destinatar…"
            defaultValue={values.q}
            onChange={(e) => set({ q: e.target.value })}
            style={{ maxWidth: 320 }}
            aria-label="Căutare"
          />
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 220 }}
            aria-label="Stare"
          >
            <option value="">Toate stările</option>
            {(Object.keys(OPERATION_STATUS) as OperationStatus[]).map((st) => (
              <option key={st} value={st}>
                {OPERATION_STATUS[st].label}
              </option>
            ))}
          </Select>
          <Select
            value={values.lockerId}
            onChange={(e) => set({ lockerId: e.target.value })}
            style={{ maxWidth: 240 }}
            aria-label="Locker"
          >
            <option value="">Toate lockerele</option>
            {lockers.data?.items.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
          {user?.orgId === null && (
            <Select
              value={values.orgId}
              onChange={(e) => set({ orgId: e.target.value })}
              style={{ maxWidth: 240 }}
              aria-label="Organizație"
            >
              <option value="">Toate organizațiile</option>
              {orgs.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <Table<Operation>
          columns={[
            {
              key: 'title',
              header: 'Operațiune / referință',
              render: (o) => (
                <>
                  <strong>{o.title}</strong>
                  <div className="small muted mono">{o.reference}</div>
                </>
              ),
            },
            {
              key: 'recipient',
              header: 'Destinatar',
              render: (o) => (
                <>
                  {recipientOf(o).name}
                  <div className="small muted">
                    {recipientOf(o).email ?? recipientOf(o).phone ?? ''}
                  </div>
                </>
              ),
            },
            {
              key: 'locker',
              header: 'Locker / compartiment',
              render: (o) => (
                <>
                  {lockerName(o.lockerId)}
                  <div className="small muted">Compartimentul {o.slotLabel}</div>
                </>
              ),
            },
            {
              key: 'status',
              header: 'Stare',
              render: (o) => <OperationStatusBadge status={o.status} />,
            },
            {
              key: 'expires',
              header: 'Termen',
              render: (o) => (
                <span
                  className={o.status === 'expired' ? '' : 'muted'}
                  style={o.status === 'expired' ? { color: 'var(--red)' } : undefined}
                >
                  {fmtDateTime(o.expiresAt)}
                </span>
              ),
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(o) => o.id}
          onRowClick={(o) => navigate(`/operatiuni/${o.id}`)}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Nicio operațiune găsită.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {creating && <OperationWizard onClose={() => setCreating(false)} />}
    </>
  );
}

export function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
  tone?: 'red' | 'orange' | 'green';
}) {
  const color =
    tone === 'red'
      ? 'var(--red)'
      : tone === 'orange'
        ? 'var(--orange)'
        : tone === 'green'
          ? 'var(--green)'
          : undefined;
  return (
    <div className={s.kpi} role="group" aria-label={label}>
      <span className={s.label}>{label}</span>
      <strong style={{ color: value && color ? color : undefined }}>
        {value === undefined ? '…' : value}
      </strong>
      {hint && <span>{hint}</span>}
    </div>
  );
}
